import React, { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faImage, faArrowLeft, faArrowRight, faTimes, faCheck, faSpinner, faRefresh } from '@fortawesome/pro-solid-svg-icons'
import { Dispatch } from '../architecture/frontend/index'
import moment from 'moment'

const AttachModal = (props) => {
  const [processedImages, setProcessedImages] = useState([])
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [isAttaching, setIsAttaching] = useState(false)

  // Get processed images on mount and when modal opens
  useEffect(() => {
    if (props.modal?.type === 'attach') {
      fetchProcessedImages()
    }
  }, [props.modal?.type])

  // Listen for updates to processed images from state
  useEffect(() => {
    const processedImages$ = Dispatch.getState('processedImages')
    const subscription = processedImages$
      .subscribe((images) => {
        // Only update state if the data has actually changed to prevent unnecessary re-renders
        const filteredImages = images && Array.isArray(images) ? images.filter(img => !img.saved) : []
        
        setProcessedImages(prevImages => {
          // Compare arrays to avoid unnecessary state updates
          if (JSON.stringify(prevImages) !== JSON.stringify(filteredImages)) {
            return filteredImages
          }
          return prevImages
        })
        
        setIsLoading(false)
      })

    return () => subscription.unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Empty dependency array intentional - we want this subscription to be created once on mount

  const fetchProcessedImages = () => {
    setIsLoading(true)
    setError('')
    Dispatch.nextAction('processImage.retrieve')
  }

  const closeModal = () => {
    Dispatch.nextAction('__.Modal.close')
  }

  const getCurrentImage = () => {
    return processedImages[currentImageIndex] || null
  }

  const goToPrevious = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1)
    }
  }

  const goToNext = () => {
    if (currentImageIndex < processedImages.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1)
    }
  }

  const attachImageToSample = () => {
    const currentImage = getCurrentImage()
    if (!currentImage || !props.shipment) {
      setError('No image selected or no shipment available')
      return
    }

    setIsAttaching(true)
    setError('')

    Dispatch.nextAction('shipment.set', {
      type: 'shipmentShippedDate',
      value: currentImage.dateReceived
    })

    if (currentImage.lineNumber && currentImage.poNumber) {
      Dispatch.nextAction('shipment.set', {
        type: 'shipmentCustomId',
        value: currentImage.poNumber + '-' + currentImage.lineNumber
      })
    }
    else if (currentImage.poNumber) {
      Dispatch.nextAction('shipment.set', {
        type: 'shipmentCustomId',
        value: currentImage.poNumber
      })
    }

    if (currentImage.orderNumber) {
      Dispatch.nextAction('shipment.set', {
        type: 'claimId',
        value: currentImage.orderNumber
      })
    }
    else if (currentImage.ffmNumber) {
      Dispatch.nextAction('shipment.set', {
        type: 'claimId',
        value: currentImage.ffmNumber
      })
    }
    
    // Handle weight if there's a field for it in your system
    if (currentImage.weight) {
      // TODO: Add weight field submission when available
      console.log('Weight value to submit:', currentImage.weight)
    }

    setTimeout(() => {
    Dispatch.nextAction('shipment.set', {
      type: 'key',
      value: Math.random()
    })
  }, 10)



    // Mark the image as saved
    Dispatch.nextAction('processImage.save', { id: currentImage.id })

    // Remove the image from our local list
    setProcessedImages(prev => prev.filter(img => img.id !== currentImage.id))
    
    // Adjust current index if needed
    if (currentImageIndex >= processedImages.length - 1) {
      setCurrentImageIndex(Math.max(0, processedImages.length - 2))
    }

    setIsAttaching(false)
    
    // Show success message briefly then close modal
    setTimeout(() => {
      closeModal()
    }, 1000)
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return moment(dateString).format('MMM DD, YYYY')
  }

  if (isLoading) {
    return (
      <div className='w-100 avenir dark-gray'>
        <div className='w-100 pa3 flex items-center justify-between bg-evonik-purple'>
          <div className='flex items-center'>
            <FontAwesomeIcon className='f6 white' icon={faImage} />
            <div className='pl2 fw7 f6 white'>Attach Processed Image</div>
          </div>
          <div className='fw7 f5 white pointer hover-light-gray' onClick={closeModal}>
            <FontAwesomeIcon icon={faTimes} />
          </div>
        </div>
        <div className='pa4 tc'>
          <FontAwesomeIcon className='blue f3 mb3' icon={faSpinner} spin />
          <div className='fw6 gray'>Loading processed images...</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className='w-100 avenir dark-gray'>
        <div className='w-100 pa3 flex items-center justify-between bg-evonik-purple'>
          <div className='flex items-center'>
            <FontAwesomeIcon className='f6 white' icon={faImage} />
            <div className='pl2 fw7 f6 white'>Attach Processed Image</div>
          </div>
          <div className='fw7 f5 white pointer hover-light-gray' onClick={closeModal}>
            <FontAwesomeIcon icon={faTimes} />
          </div>
        </div>
        <div className='pa4'>
          <div className='pa3 bg-light-red ba b--red br2'>
            <div className='red fw6'>Error:</div>
            <div className='red'>{error}</div>
            <div className='pt3'>
              <button 
                className='bg-evonik-purple white pa2 ph3 br3 fw6 pointer hover-bg-evonik-light-purple bn f6 mr2'
                onClick={fetchProcessedImages}
              >
                <FontAwesomeIcon className='pr2' icon={faRefresh} />
                Retry
              </button>
              <button 
                className='bg-gray white pa2 ph3 br3 fw6 pointer hover-bg-dark-gray bn f6'
                onClick={closeModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (processedImages.length === 0) {
    return (
      <div className='w-100 avenir dark-gray'>
        <div className='w-100 pa3 flex items-center justify-between bg-evonik-purple'>
          <div className='flex items-center'>
            <FontAwesomeIcon className='f6 white' icon={faImage} />
            <div className='pl2 fw7 f6 white'>Attach Processed Image</div>
          </div>
          <div className='fw7 f5 white pointer hover-light-gray' onClick={closeModal}>
            <FontAwesomeIcon icon={faTimes} />
          </div>
        </div>
        <div className='pa4 tc'>
          <div className='mb4'>
            <FontAwesomeIcon className='gray f2 mb3' icon={faImage} />
            <h3 className='gray fw6'>No Processed Images Available</h3>
            <p className='gray fw4'>
              There are no processed images available to attach. Use the scan feature to capture and process images first.
            </p>
          </div>
          <button 
            className='bg-evonik-purple white pa3 ph4 br3 fw6 pointer hover-bg-evonik-light-purple bn f5'
            onClick={closeModal}
          >
            Close
          </button>
        </div>
      </div>
    )
  }

  const currentImage = getCurrentImage()
  const hasPrevious = currentImageIndex > 0
  const hasNext = currentImageIndex < processedImages.length - 1

  return (
    <div className='w-100 avenir dark-gray'>
      <div className='w-100 pa3 flex items-center justify-between bg-evonik-purple'>
        <div className='flex items-center'>
          <FontAwesomeIcon className='f6 white' icon={faImage} />
          <div className='pl2 fw7 f6 white'>Attach Processed Image</div>
          <div className='pl2 fw6 f6 white'>
            ({currentImageIndex + 1} of {processedImages.length})
          </div>
        </div>
        <div className='fw7 f5 white pointer hover-light-gray' onClick={closeModal}>
          <FontAwesomeIcon icon={faTimes} />
        </div>
      </div>

      <div className='pa4'>
        {currentImage && (
          <div className='tc'>
            {/* Navigation controls */}
            <div className='flex justify-between items-center mb4'>
              <button 
                className={`pa2 ph3 br3 fw6 bn f6 ${hasPrevious ? 'bg-evonik-light-purple white pointer hover-bg-evonik-purple' : 'bg-light-gray gray'}`}
                onClick={goToPrevious}
                disabled={!hasPrevious}
              >
                <FontAwesomeIcon className='pr2' icon={faArrowLeft} />
                Previous
              </button>
              
              <div className='fw6 f5 evonik-purple'>
                Image {currentImageIndex + 1} of {processedImages.length}
              </div>
              
              <button 
                className={`pa2 ph3 br3 fw6 bn f6 ${hasNext ? 'bg-evonik-light-purple white pointer hover-bg-evonik-purple' : 'bg-light-gray gray'}`}
                onClick={goToNext}
                disabled={!hasNext}
              >
                Next
                <FontAwesomeIcon className='pl2' icon={faArrowRight} />
              </button>
            </div>

            {/* Image details */}
            <div className='ba b--moon-gray br3 pa4 bg-white mb4'>
              <h3 className='evonik-purple fw6 mb3'>Processed Image Data</h3>
              
              <div className='tl'>
                <div className='flex flex-wrap'>
                  <div className='w-50 pr3 mb3'>
                    <div className='fw6 gray f6 mb1'>PO Number:</div>
                    <div className='fw6 dark-gray f5'>{currentImage.poNumber || 'Not detected'}</div>
                  </div>
                  
                  <div className='w-50 pl3 mb3'>
                    <div className='fw6 gray f6 mb1'>Line Number:</div>
                    <div className='fw6 dark-gray f5'>{currentImage.lineNumber || 'Not detected'}</div>
                  </div>
                  
                  <div className='w-50 pr3 mb3'>
                    <div className='fw6 gray f6 mb1'>Order Number:</div>
                    <div className='fw6 dark-gray f5'>{currentImage.orderNumber || 'Not detected'}</div>
                  </div>
                  
                  <div className='w-50 pl3 mb3'>
                    <div className='fw6 gray f6 mb1'>Weight:</div>
                    <div className='fw6 dark-gray f5'>{currentImage.weight || 'Not detected'}</div>
                  </div>
                  
                  <div className='w-50 pr3 mb3'>
                    <div className='fw6 gray f6 mb1'>FFM Number:</div>
                    <div className='fw6 dark-gray f5'>{currentImage.ffmNumber || 'Not detected'}</div>
                  </div>
                  
                  <div className='w-50 pl3 mb3'>
                    <div className='fw6 gray f6 mb1'>Date Received:</div>
                    <div className='fw6 dark-gray f5'>{formatDate(currentImage.dateReceived)}</div>
                  </div>
                </div>
                
                
              </div>
            </div>

            

            {/* Attach button */}
            <div className='tc'>
              {isAttaching ? (
                <div className='pa3'>
                  <FontAwesomeIcon className='blue f4 mb2' icon={faSpinner} spin />
                  <div className='fw6 blue'>Attaching image data...</div>
                </div>
              ) : (
                <button 
                  className='bg-green white pa3 ph4 br3 fw6 pointer hover-bg-dark-green bn f5'
                  onClick={attachImageToSample}
                  disabled={!props.shipment}
                >
                  <FontAwesomeIcon className='pr2' icon={faCheck} />
                  Attach to Current Sample
                </button>
              )}
              
              {!props.shipment && (
                <div className='pt2'>
                  <div className='red f6 fw6'>No sample selected. Please create or select a shipment first.</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AttachModal
